import {GridModel} from '@xh/hoist/cmp/grid';
import {HoistModel, LoadSpec, managed, TaskObserver, XH} from '@xh/hoist/core';
import {action, observable} from '@xh/hoist/mobx';
import {Deposit} from '../../../../data/DataTypes';
import {asServerException} from '../../../../../core/ServiceUtils';
import {fmtDate, fmtDateTimeSec, fmtNumber} from '@xh/hoist/format';
import {FormModel} from '@xh/hoist/cmp/form';
import {required} from '@xh/hoist/data';
import {dateOnlyToDateTime} from '../../../../data/Utils';

export class DepositsDialogModel extends HoistModel {
    apiTask = TaskObserver.trackLast();
    @observable accessor isOpen: boolean;
    @managed gridModel = this.createGridModel();
    @managed editorModel = new DepositEditorModel();
    private portfolioId: number;

    @action open(portfolioId: number) {
        this.gridModel.clear();
        this.isOpen = true;
        this.portfolioId = portfolioId;
        this.loadAsync();
    }

    @action close() {
        this.isOpen = false;
    }

    get selectedDeposit(): Deposit {
        return this.gridModel.selectedRecord?.raw as Deposit;
    }

    async addDepositAsync() {
        let deposit = await this.editorModel.openAsync({id: 0, portfolioId: this.portfolioId});
        if (!deposit) return;

        try {
            deposit = await XH.portfolioService.saveDepositAsync(deposit).linkTo(this.apiTask);
            await this.refreshAsync();
            this.gridModel.selectAsync(deposit.id, {ensureVisible: true});
        } catch (e) {
            XH.handleException(asServerException(e), {title: 'Error saving deposit'});
        }
    }

    async cloneDepositAsync() {
        const orig = {...this.selectedDeposit, id: 0};
        let deposit = await this.editorModel.openAsync(orig);
        if (!deposit) return;

        try {
            deposit = await XH.portfolioService.saveDepositAsync(deposit).linkTo(this.apiTask);
            await this.refreshAsync();
            this.gridModel.selectAsync(deposit.id, {ensureVisible: true});
        } catch (e) {
            XH.handleException(asServerException(e), {title: 'Error saving deposit'});
        }
    }

    async editDepositAsync() {
        const deposit = await this.editorModel.openAsync(this.selectedDeposit);
        if (!deposit) return;

        try {
            await XH.portfolioService.saveDepositAsync(deposit).linkTo(this.apiTask);
            await this.refreshAsync();
        } catch (e) {
            XH.handleException(asServerException(e), {title: 'Error saving deposit'});
        }
    }

    async deleteDepositAsync() {
        const confirm = await XH.confirm({
            title: 'Delete Deposit?',
            message: `Are you sure you want to delete the deposit?`,
            confirmProps: {
                intent: 'danger',
                text: 'Delete Deposit'
            }
        });
        if (!confirm) return;

        try {
            await XH.portfolioService.deleteDepositAsync(this.selectedDeposit.id).linkTo(this.apiTask);
            this.refreshAsync();
        } catch (e) {
            XH.handleException(asServerException(e), {title: 'Error deleting deposit'});
        }
    }

    refresh() {
        this.refreshAsync();
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        if (!this.portfolioId) return;

        try {
            const deposits = await XH.portfolioService.fetchDepositsAsync(this.portfolioId).linkTo(this.apiTask);
            const data = deposits.map(d => {
                const dt = dateOnlyToDateTime(d.asOfDate);
                return {...d, asOfDate: dt};
            });
            this.gridModel.loadData(data);
        } catch (e) {
            XH.handleException(asServerException(e), {title: 'Error loading deposits'});
        }
    }

    private createGridModel(): GridModel {
        return new GridModel({
            // A shadow user only views deposits
            onRowDoubleClicked: () => {
                if (!XH.entitlementService.isShadow) this.editDepositAsync();
            },
            sortBy: 'asOfDate',
            columns: [
                {field: 'id', hidden: true},
                {field: 'portfolioId', hidden: true},
                {field: 'asOfDate', width: 110, renderer: v => fmtDate(v)},
                {
                    field: 'amount',
                    headerName: 'Amount, $',
                    width: 120,
                    align: 'right',
                    renderer: v => fmtNumber(v, {ledger: true, precision: 2})
                },
                {field: 'notes', flex: 1},
                {field: 'updated', width: 170, renderer: v => fmtDateTimeSec(v)},
                {field: 'updatedBy', displayName: 'By', width: 100}
            ]
        });
    }
}

export class DepositEditorModel extends HoistModel {
    @observable accessor isOpen: boolean;
    @observable accessor isNew: boolean;
    @managed formModel = new FormModel({
        fields: [
            {name: 'id'},
            {name: 'portfolioId'},
            {name: 'asOfDate', rules: [required]},
            {name: 'amount', rules: [required]},
            {name: 'notes'},
            {name: 'timestamp'}
        ]
    });
    private resolver: (value: Deposit) => void;

    @action async openAsync(deposit: Deposit): Promise<Deposit> {
        this.isOpen = true;
        this.isNew = !deposit.id;
        this.formModel.init(deposit);
        this.formModel.validateAsync();

        return new Promise(r => (this.resolver = r));
    }

    @action close() {
        this.isOpen = false;
        this.resolver(null);
    }

    @action save() {
        const deposit = this.formModel.getData() as Deposit;
        this.resolver(deposit);
        this.isOpen = false;
    }
}
